export const SEPARATOR = "/";
export const ELLIPSIS = "…";
const ZERO_WIDTH_SPACE = "\u200B";

/**
 * The full path as one string, with a zero-width space after each separator so it can wrap
 * between segments. Used by the expanded `expandable` variant.
 */
export const toWrappablePath = (names: string[]) => names.join(`${SEPARATOR}${ZERO_WIDTH_SPACE}`);

type Measure = (text: string) => number;

export type SpacePathLayout = {
  /** Render a leading `…/` marker for the ancestor segments that were dropped. */
  leadingEllipsis: boolean;
  /** Segment names to render, in order. The last one is always the direct space. */
  segments: string[];
  /** Let the (sole remaining) last segment overflow with a CSS ellipsis. */
  ellipsizeLast: boolean;
  /** Whether anything was hidden/shortened, so callers can show a tooltip. */
  truncated: boolean;
};

/** Whether two layouts would render identically, so the hook can skip a re-render. */
export const areLayoutsEqual = (a: SpacePathLayout, b: SpacePathLayout) =>
  a.leadingEllipsis === b.leadingEllipsis &&
  a.ellipsizeLast === b.ellipsizeLast &&
  a.truncated === b.truncated &&
  a.segments.length === b.segments.length &&
  a.segments.every((segment, index) => segment === b.segments[index]);

const join = (parts: string[]) => parts.join(SEPARATOR);

const withLeadingEllipsis = (parts: string[]) => `${ELLIPSIS}${SEPARATOR}${join(parts)}`;

/**
 * Largest leading slice of `name` (plus a trailing ellipsis) that still `fits` — e.g. turning
 * `root` into `roo…`, `ro…`, or `r…`. `null` when not even a single character fits.
 */
const largestAbbreviation = (name: string, fits: (candidate: string) => boolean): string | null => {
  for (let length = name.length - 1; length >= 1; length -= 1) {
    const candidate = `${name.slice(0, length)}${ELLIPSIS}`;
    if (fits(candidate)) {
      return candidate;
    }
  }

  return null;
};

/**
 * Resolve how a space path should render within `availableWidth`.
 *
 * Prefer the end of the path (leaf and its ancestors). When the full path does not fit:
 * 1. pack complete trailing segments under a leading `…/` while they fit,
 * 2. abbreviate the next (boundary) segment into the remaining width,
 * 3. if only the leaf remains and it overflows, keep `…/` and CSS-ellipsis the leaf
 *    whenever `…/` plus one character of the leaf still fits,
 * 4. otherwise drop `…/` and CSS-ellipsis the leaf alone.
 */
export const computeSpacePathLayout = (
  names: string[],
  availableWidth: number,
  measure: Measure
): SpacePathLayout => {
  if (names.length === 0) {
    return { leadingEllipsis: false, segments: [], ellipsizeLast: false, truncated: false };
  }

  const lastIndex = names.length - 1;
  const last = names[lastIndex];

  // A single segment can only ever be shortened by CSS.
  if (names.length === 1) {
    const overflows = measure(last) > availableWidth;

    return {
      leadingEllipsis: false,
      segments: [last],
      ellipsizeLast: overflows,
      truncated: overflows,
    };
  }

  if (measure(join(names)) <= availableWidth) {
    return { leadingEllipsis: false, segments: names, ellipsizeLast: false, truncated: false };
  }

  // Pack from the end so the leaf path stays visible; drop the head behind `…/`.
  const suffix: string[] = [last];

  for (let index = lastIndex - 1; index >= 0; index -= 1) {
    const trial = [names[index], ...suffix];

    if (measure(withLeadingEllipsis(trial)) <= availableWidth) {
      suffix.unshift(names[index]);
      continue;
    }

    // Boundary segment does not fit fully — shorten it into the remaining width.
    const abbreviated = largestAbbreviation(
      names[index],
      (candidate) => measure(withLeadingEllipsis([candidate, ...suffix])) <= availableWidth
    );

    if (abbreviated) {
      return {
        leadingEllipsis: true,
        segments: [abbreviated, ...suffix],
        ellipsizeLast: false,
        truncated: true,
      };
    }

    break;
  }

  if (suffix.length === 1) {
    if (measure(withLeadingEllipsis([last])) <= availableWidth) {
      return {
        leadingEllipsis: true,
        segments: [last],
        ellipsizeLast: false,
        truncated: true,
      };
    }

    // `…/` plus one character of the leaf (CSS ellipsis does the rest).
    const minLeafWithLeading = withLeadingEllipsis([`${last.slice(0, 1)}${ELLIPSIS}`]);

    if (measure(minLeafWithLeading) <= availableWidth) {
      return {
        leadingEllipsis: true,
        segments: [last],
        ellipsizeLast: true,
        truncated: true,
      };
    }

    return {
      leadingEllipsis: false,
      segments: [last],
      ellipsizeLast: true,
      truncated: true,
    };
  }

  return {
    leadingEllipsis: true,
    segments: suffix,
    ellipsizeLast: false,
    truncated: true,
  };
};

let sharedCanvasContext: CanvasRenderingContext2D | null | undefined;

/**
 * Text-width measurer backed by a shared canvas, using the font resolved from `element`.
 * `null` when a 2D canvas context is unavailable (e.g. jsdom).
 */
export const createCanvasMeasurer = (element: HTMLElement): Measure | null => {
  if (sharedCanvasContext === undefined) {
    sharedCanvasContext = document.createElement("canvas").getContext("2d");
  }

  if (!sharedCanvasContext) {
    return null;
  }

  const { fontStyle, fontWeight, fontSize, fontFamily } = window.getComputedStyle(element);
  const font = `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
  const context = sharedCanvasContext;

  return (text) => {
    context.font = font;
    return context.measureText(text).width;
  };
};
